"use client"

import { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Switch } from "@/components/ui/switch"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction, TabHeader, TabSurface, TonePill } from "@/components/graphite/surface"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Edit, Eye, Loader2, Mail, Plus, Save, Star, StarOff, Trash2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import {
  getGetSignatureQueryKey,
  useDeleteSignatureSignatureId,
  useGetSignature,
  usePostSignature,
  usePostSignatureSignatureIdSetDefault,
  usePutSignatureSignatureId,
} from "openapi/api/endpoints"
import type { EmailSignature } from "openapi/api/model"

interface UiEmailSignature {
  id: string
  name?: string
  signature: string
  htmlSignature: string
  isDefault: boolean
  isActive: boolean
}

const mapApiToUi = (item: EmailSignature, index: number): UiEmailSignature => ({
  id: item.id ?? `signature-${index}`,
  name: (item as any).name ?? undefined,
  signature: ((item as any).text ?? item.text_signature ?? ""),
  htmlSignature: ((item as any).html ?? item.html_signature ?? ""),
  isDefault: item.is_default ?? false,
  isActive: item.is_active ?? true,
})

const mapUiToApi = (item: UiEmailSignature): EmailSignature => ({
  id: item.id,
  name: item.name || null,
  text_signature: item.signature,
  html_signature: item.htmlSignature || null,
  is_default: item.isDefault,
  is_active: item.isActive,
} as any)

const getCurrentOrganizationId = () => {
  if (typeof window === "undefined") return null
  return localStorage.getItem("currentOrganization")
}

const getSignatureExcerpt = (signature: string) => {
  const meaningfulLine = signature
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0 && !/^[-_]{2,}$/.test(line))

  if (!meaningfulLine) return "No signature text"
  return meaningfulLine.length > 72 ? `${meaningfulLine.slice(0, 72)}...` : meaningfulLine
}

const getSignatureTitle = (signature: UiEmailSignature, index: number) => {
  const named = signature.name?.trim()
  if (named) return named
  if (signature.isDefault) return "Default Signature"
  return `Signature ${index + 1}`
}

export function EmailSignaturesSettings() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const signatureQueryKey = useMemo(() => getGetSignatureQueryKey(), [])

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null)
  const [newSignatureName, setNewSignatureName] = useState("")
  const [newSignature, setNewSignature] = useState("")
  const [newHtmlSignature, setNewHtmlSignature] = useState("")
  const [signatures, setSignatures] = useState<UiEmailSignature[]>([])

  const { data, isLoading, isError, refetch } = useGetSignature()
  const { mutateAsync: createSignature, isPending: isCreating } = usePostSignature()
  const { mutateAsync: updateSignature, isPending: isUpdating } = usePutSignatureSignatureId()
  const { mutateAsync: deleteSignature, isPending: isDeleting } = useDeleteSignatureSignatureId()
  const { mutateAsync: setDefaultSignature, isPending: isSettingDefault } = usePostSignatureSignatureIdSetDefault()

  useEffect(() => {
    const mapped = (data ?? []).map(mapApiToUi)
    setSignatures(mapped)
  }, [data])

  const syncOptimisticState = (next: UiEmailSignature[]) => {
    setSignatures(next)
    queryClient.setQueryData(signatureQueryKey, next.map(mapUiToApi))
  }

  const handleSetDefault = async (index: number) => {
    const target = signatures[index]
    if (!target?.id || target.isDefault) return

    const previous = signatures
    const optimistic = signatures.map((item, idx) => ({ ...item, isDefault: idx === index }))
    syncOptimisticState(optimistic)

    try {
      await setDefaultSignature({ signatureId: target.id })
      toast({
        title: "Default signature updated",
        description: "Default signature saved.",
      })
    } catch {
      syncOptimisticState(previous)
      toast({
        title: "Failed to set default signature",
        description: "Your signature was reverted.",
        variant: "destructive",
      })
    } finally {
      await queryClient.invalidateQueries({ queryKey: signatureQueryKey })
    }
  }

  const handleSaveSignature = async (index: number) => {
    const target = signatures[index]
    if (!target?.id) return

    const previous = data?.map(mapApiToUi) ?? []
    syncOptimisticState(signatures)

    try {
      await updateSignature({
        signatureId: target.id,
        data: {
          text_signature: target.signature,
          html_signature: target.htmlSignature || null,
          is_active: target.isActive,
          name: target.name?.trim() || null,
          text: target.signature,
          html: target.htmlSignature || null,
        } as any,
      })
      setEditingIndex(null)
      toast({
        title: "Signature saved",
        description: "Your email signature has been updated.",
      })
    } catch {
      syncOptimisticState(previous)
      toast({
        title: "Failed to save signature",
        description: "Your changes were reverted.",
        variant: "destructive",
      })
    } finally {
      await queryClient.invalidateQueries({ queryKey: signatureQueryKey })
    }
  }

  const handleDeleteSignature = async (index: number) => {
    const target = signatures[index]
    if (!target?.id) return

    if (target.isDefault) {
      toast({
        title: "Cannot delete default signature",
        description: "Set another signature as default first.",
        variant: "destructive",
      })
      return
    }

    const previous = signatures
    const optimistic = signatures.filter((_, idx) => idx !== index)
    syncOptimisticState(optimistic)

    try {
      await deleteSignature({ signatureId: target.id })
      setDeleteConfirmIndex(null)
      toast({
        title: "Signature deleted",
        description: "The signature has been removed.",
      })
    } catch {
      syncOptimisticState(previous)
      toast({
        title: "Failed to delete signature",
        description: "Your signature was restored.",
        variant: "destructive",
      })
    } finally {
      await queryClient.invalidateQueries({ queryKey: signatureQueryKey })
    }
  }

  const handleAddSignature = async () => {
    if (!newSignature.trim()) {
      toast({
        title: "Signature required",
        description: "Please enter a text signature.",
        variant: "destructive",
      })
      return
    }

    const organizationId = getCurrentOrganizationId()
    if (!organizationId) {
      toast({
        title: "Organization missing",
        description: "No active organization found in local storage.",
        variant: "destructive",
      })
      return
    }

    const previous = signatures
    const tempId = `temp-${Date.now()}`
    const isFirst = signatures.length === 0
    const optimisticEntry: UiEmailSignature = {
      id: tempId,
      name: newSignatureName.trim() || undefined,
      signature: newSignature.trim(),
      htmlSignature: newHtmlSignature.trim(),
      isDefault: isFirst,
      isActive: true,
    }
    syncOptimisticState([...signatures, optimisticEntry])

    try {
      await createSignature({
        data: {
          name: optimisticEntry.name || null,
          text_signature: optimisticEntry.signature,
          html_signature: optimisticEntry.htmlSignature || null,
          is_default: optimisticEntry.isDefault,
          text: optimisticEntry.signature,
          html: optimisticEntry.htmlSignature || null,
        } as any,
      })
      setNewSignature("")
      setNewSignatureName("")
      setNewHtmlSignature("")
      setAddDialogOpen(false)
      toast({
        title: "Signature added",
        description: "A new email signature was created.",
      })
    } catch {
      syncOptimisticState(previous)
      toast({
        title: "Failed to add signature",
        description: "The new signature was not saved.",
        variant: "destructive",
      })
    } finally {
      await queryClient.invalidateQueries({ queryKey: signatureQueryKey })
    }
  }

  const updateSignatureDraft = (index: number, patch: Partial<UiEmailSignature>) => {
    setSignatures((current) =>
      current.map((item, idx) => (idx === index ? { ...item, ...patch } : item)),
    )
  }

  const activeMutation = isCreating || isUpdating || isDeleting || isSettingDefault

  return (
    <>
      <TabSurface fill={false}>
        <TabHeader
          label="Signatures"
          count={signatures.length || undefined}
          actions={
            <PageAction
              tone="primary"
              onClick={() => setAddDialogOpen(true)}
              disabled={activeMutation}
            >
              <Plus className="size-[15px]" />
              Add Signature
            </PageAction>
          }
        />

        {isLoading ? (
          <p className="flex items-center gap-2 pt-4 text-[12.5px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Reading your signatures…
          </p>
        ) : isError ? (
          <div className="flex flex-wrap items-center gap-3 pt-4">
            <p className="text-[12.5px] text-muted-foreground">
              Your signatures cannot be read right now.
            </p>
            <QuietAction onClick={() => refetch()}>Retry</QuietAction>
          </div>
        ) : signatures.length === 0 ? (
          <div className="pt-4">
            <p className="text-[12.5px] font-medium">No signatures yet</p>
            <p className="mt-1 text-[11.5px] leading-[1.5] text-muted-foreground">
              A signature is appended to the mail you send from the desk. Add one and
              mark it default.
            </p>
          </div>
        ) : (
          /* Hairline rows in the one surface, not a card apiece. */
          <div className="divide-y">
            {signatures.map((signature, index) => (
              <SignatureRow
                key={signature.id}
                signature={signature}
                index={index}
                editing={editingIndex === index}
                busy={activeMutation}
                onPreview={() => setPreviewIndex(index)}
                onToggleEdit={() => setEditingIndex(editingIndex === index ? null : index)}
                onSetDefault={() => handleSetDefault(index)}
                onDelete={() => setDeleteConfirmIndex(index)}
                onChange={(patch) => updateSignatureDraft(index, patch)}
                onCancel={() => {
                  setSignatures((data ?? []).map(mapApiToUi))
                  setEditingIndex(null)
                }}
                onSave={() => handleSaveSignature(index)}
              />
            ))}
          </div>
        )}
      </TabSurface>

      <DialogShell
        open={previewIndex !== null}
        onOpenChange={() => setPreviewIndex(null)}
        wide
        title="Signature Preview"
        description={
          previewIndex !== null
            ? getSignatureTitle(signatures[previewIndex], previewIndex)
            : undefined
        }
        footer={
          <>
            <QuietAction size="md" onClick={() => setPreviewIndex(null)}>
              Close
            </QuietAction>
            <PageAction
              tone="primary"
              onClick={() => {
                if (previewIndex !== null) {
                  setEditingIndex(previewIndex)
                  setPreviewIndex(null)
                }
              }}
            >
              Edit Signature
            </PageAction>
          </>
        }
      >
        {previewIndex !== null && (
          <SignatureBody signature={signatures[previewIndex]} tall />
        )}
      </DialogShell>

      <DialogShell
        open={addDialogOpen}
        onOpenChange={(open) => {
          setAddDialogOpen(open)
          if (!open) {
            setNewSignatureName("")
            setNewSignature("")
            setNewHtmlSignature("")
          }
        }}
        wide
        title="Add Email Signature"
        description="Write the markup on the left; the right is what lands in the mail."
        footer={
          <>
            <QuietAction size="md" onClick={() => setAddDialogOpen(false)} disabled={activeMutation}>
              Cancel
            </QuietAction>
            <PageAction tone="primary" onClick={handleAddSignature} disabled={activeMutation}>
              Add Signature
            </PageAction>
          </>
        }
      >
        <div className="space-y-5">
          <FieldShell label="Signature name">
            <input
              value={newSignatureName}
              onChange={(event) => setNewSignatureName(event.target.value)}
              placeholder="e.g. Executive Signature"
              aria-label="Signature name"
              className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
            />
          </FieldShell>
          <FieldShell label="Text signature">
            <textarea
              value={newSignature}
              onChange={(event) => setNewSignature(event.target.value)}
              rows={5}
              placeholder={"Best regards,\n\nYour Name\nTitle\nCompany"}
              aria-label="Text signature"
              className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 font-mono text-[12px] leading-[1.6]")}
            />
          </FieldShell>
          <HtmlEditor
            value={newHtmlSignature}
            onChange={setNewHtmlSignature}
            fallback={newSignature}
          />
        </div>
      </DialogShell>

      <AlertDialog open={deleteConfirmIndex !== null} onOpenChange={() => setDeleteConfirmIndex(null)}>
        <AlertDialogContent>
          {/*
            Still an `AlertDialog` rather than the shell: it carries
            `role="alertdialog"` and traps focus on its cancel, which is the
            right behaviour for a destructive confirm. Only the type comes
            across.
          */}
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[14px] font-semibold leading-[1.3] tracking-[-0.01em]">
              Delete signature?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12px] leading-[1.5]">
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmIndex !== null && handleDeleteSignature(deleteConfirmIndex)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={activeMutation}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * A signature as it will actually arrive.
 *
 * An `<iframe srcDoc>`, not `dangerouslySetInnerHTML`, for three reasons that
 * all point the same way. It renders the markup rather than printing it, which
 * is the whole point of a signature preview and is what the panel used to get
 * wrong — it showed you your own source in a mono block. It is isolated, so the
 * app's stylesheet does not restyle the signature into something the recipient
 * will never see, and the signature's own CSS cannot reach back out into the
 * page. And `sandbox=""` with no `allow-scripts` means nothing in it executes,
 * which matters because this HTML is typed in by hand and stored, and the app
 * has no sanitizer anywhere in it.
 */
function SignatureFrame({ html, className }: { html: string; className?: string }) {
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    :root { color-scheme: light }
    body { margin: 0; padding: 12px; background: #fff; color: #111;
           font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif }
    img { max-width: 100% }
  </style></head><body>${html}</body></html>`

  return (
    <iframe
      title="Signature preview"
      sandbox=""
      srcDoc={doc}
      className={cn("w-full rounded-[8px] border bg-white", className)}
    />
  )
}

/**
 * What a signature looks like, preferring the HTML the recipient gets.
 *
 * The plain-text body is the fallback for a signature that has no HTML — it is
 * still what goes out on a text-only mail — and it renders as text rather than
 * pretending to be rendered markup.
 */
function SignatureBody({ signature, tall }: { signature: UiEmailSignature; tall?: boolean }) {
  const html = signature.htmlSignature?.trim()
  if (html) return <SignatureFrame html={html} className={tall ? "h-[260px]" : "h-[112px]"} />
  return (
    <pre
      className={cn(
        "overflow-auto whitespace-pre-wrap rounded-[8px] border bg-muted/30 p-3 font-sans text-[12px] leading-[1.6]",
        tall ? "max-h-[260px]" : "max-h-[112px]",
      )}
    >
      {signature.signature}
    </pre>
  )
}

/** The markup and what it becomes, side by side once there is room for both. */
function HtmlEditor({
  value,
  onChange,
  fallback,
}: {
  value: string
  onChange: (value: string) => void
  fallback?: string
}) {
  const preview = value.trim()
  return (
    <div className="grid gap-4 @xl:grid-cols-2">
      <FieldShell label="HTML signature">
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={8}
          placeholder={"<p>Best regards,<br/>Your Name<br/>Title</p>"}
          aria-label="HTML signature"
          className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 font-mono text-[12px] leading-[1.6]")}
        />
      </FieldShell>
      <div className="min-w-0">
        <div className="text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Preview
        </div>
        <div className="mt-[9px]">
          {preview ? (
            <SignatureFrame html={preview} className="h-[186px]" />
          ) : (
            <div className="flex h-[186px] items-center justify-center rounded-[8px] border border-dashed px-4 text-center text-[11.5px] leading-[1.5] text-muted-foreground">
              {fallback?.trim()
                ? "No HTML yet — the text signature above goes out instead."
                : "What you type on the left renders here."}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** One signature: what it is, what it looks like, and what you can do to it. */
function SignatureRow({
  signature,
  index,
  editing,
  busy,
  onPreview,
  onToggleEdit,
  onSetDefault,
  onDelete,
  onChange,
  onCancel,
  onSave,
}: {
  signature: UiEmailSignature
  index: number
  editing: boolean
  busy: boolean
  onPreview: () => void
  onToggleEdit: () => void
  onSetDefault: () => void
  onDelete: () => void
  onChange: (patch: Partial<UiEmailSignature>) => void
  onCancel: () => void
  onSave: () => void
}) {
  const title = getSignatureTitle(signature, index)
  const iconButton =
    "flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"

  return (
    <div className={cn("py-3.5", !signature.isActive && !editing && "opacity-60")}>
      <div className="flex flex-col gap-3 @lg:flex-row @lg:items-start @lg:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex size-8 flex-none items-center justify-center rounded-full bg-primary/10">
            <Mail className="size-4 text-brand-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-[12.5px] font-medium leading-[1.3]">{title}</span>
              {signature.isDefault && (
                <TonePill hex={stageHex("hired")} compact>
                  Default
                </TonePill>
              )}
              {!signature.isActive && (
                <TonePill hex={stageHex("longlist")} compact>
                  Inactive
                </TonePill>
              )}
            </div>
            <p className="mt-1 truncate text-[11.5px] leading-[1.4] text-muted-foreground">
              {getSignatureExcerpt(signature.signature)}
            </p>
          </div>
        </div>

        <div className="flex flex-none items-center gap-0.5 @lg:ml-auto">
          <button type="button" onClick={onPreview} aria-label={`Preview ${title}`} className={iconButton}>
            <Eye className="size-4" />
          </button>
          <button
            type="button"
            onClick={onSetDefault}
            disabled={signature.isDefault || busy}
            aria-label={signature.isDefault ? `${title} is the default` : `Make ${title} the default`}
            className={iconButton}
          >
            {signature.isDefault ? (
              <Star className="size-4 fill-current text-brand-ink" />
            ) : (
              <StarOff className="size-4" />
            )}
          </button>
          <button
            type="button"
            onClick={onToggleEdit}
            disabled={busy}
            aria-label={`Edit ${title}`}
            className={iconButton}
          >
            <Edit className="size-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={signature.isDefault || busy}
            aria-label={signature.isDefault ? "The default cannot be deleted" : `Delete ${title}`}
            className={cn(iconButton, "hover:text-destructive")}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      {/* Closed, the row still shows what the signature looks like — the point
          of the panel is which one goes out, and a name alone does not say. */}
      {!editing && (
        <div className="mt-3 @lg:pl-11">
          <SignatureBody signature={signature} />
        </div>
      )}

      {editing && (
        <div className="mt-4 space-y-5 border-t pt-4 @lg:pl-11">
          <FieldShell label="Signature name">
            <input
              value={signature.name ?? ""}
              onChange={(event) => onChange({ name: event.target.value })}
              placeholder="e.g. Executive Signature"
              aria-label="Signature name"
              className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
            />
          </FieldShell>

          <FieldShell label="Text signature">
            <textarea
              value={signature.signature}
              onChange={(event) => onChange({ signature: event.target.value })}
              rows={6}
              placeholder="Enter your email signature..."
              aria-label="Text signature"
              className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 font-mono text-[12px] leading-[1.6]")}
            />
          </FieldShell>

          <HtmlEditor
            value={signature.htmlSignature ?? ""}
            onChange={(html) => onChange({ htmlSignature: html })}
            fallback={signature.signature}
          />

          <div className="flex flex-col gap-3 border-t pt-3.5 @lg:flex-row @lg:items-center">
            <label className="flex items-center gap-2.5">
              <Switch
                checked={signature.isActive}
                onCheckedChange={(value) => onChange({ isActive: value })}
              />
              <span className="min-w-0">
                <span className="block text-[12px] font-medium leading-[1.3]">Active</span>
                <span className="block text-[11px] leading-[1.4] text-muted-foreground">
                  Available when sending mail
                </span>
              </span>
            </label>
            <div className="flex items-center gap-2 @lg:ml-auto">
              <QuietAction onClick={onCancel} disabled={busy}>
                Cancel
              </QuietAction>
              <PageAction tone="primary" onClick={onSave} disabled={busy} className="h-8">
                <Save className="size-[14px]" />
                Save
              </PageAction>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
